import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { sendEmailVerification } from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { auth } from "../../../../../config/firebaseConfig";
import { login } from "../../../../../redux/features/authSlice";
import dbService from "../../../../../firebase/dbService";
import { Button } from "../../../../../components";
import { toast } from "react-toastify";

const EmailVerificationSentView = ({
  email,
  name = "",
  registerAs = "parent",
  password = "",
  schoolId = null,
  onBack,
  getDashboardTargetUrl,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [resendCooldown, setResendCooldown] = useState(60);
  const [resendLoading, setResendLoading] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [error, setError] = useState(null);

  // Cooldown countdown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleCheckEmailVerification = useCallback(
    async (isManual = true) => {
      setCheckingVerification(true);
      setError(null);
      try {
        if (!auth.currentUser) {
          if (isManual) toast.error("Session expired. Please try signing in.");
          return;
        }

        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          const user = auth.currentUser;
          const resolvedName = user.displayName || name?.trim() || "";

          const existingUserMeta = await dbService.getdocument("user", user.uid).catch(() => null);
          if (!existingUserMeta) {
            // 1. Create master user metadata document
            const userMeta = {
              uid: user.uid,
              email: user.email || email,
              name: resolvedName,
              accountType: registerAs,
              createdAt: Timestamp.now(),
            };
            await dbService.createDocumentWithId("user", userMeta, user.uid);

            // 2. Create role-specific document (parents or students)
            const roleCollection = registerAs === "student" ? "students" : "parents";
            const roleDocData = {
              uid: user.uid,
              email: user.email || email,
              name: resolvedName,
              accountType: registerAs,
              createdAt: Timestamp.now(),
            };
            if (schoolId) {
              roleDocData.schoolId = schoolId;
            }
            await dbService.createDocumentWithId(roleCollection, roleDocData, user.uid);

            // Flag dashboard to auto-open WhatsApp phone verification dialog on first load
            sessionStorage.setItem("open_verify_phone_modal_on_load", "true");
          }

          // 3. Dispatch Redux login to populate store
          await dispatch(login({ email: user.email || email, password })).unwrap().catch(() => null);

          toast.success("Email verified successfully! Welcome to Practido.");
          const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
          navigate(targetUrl);
        } else if (isManual) {
          toast.info("Email is not verified yet. Please check your inbox (and spam folder) and click the link.");
        }
      } catch (err) {
        console.error("Check email verification error:", err);
        if (isManual) {
          setError(err.message || "Failed to verify email status. Please try again.");
        }
      } finally {
        setCheckingVerification(false);
      }
    },
    [email, name, registerAs, password, schoolId, dispatch, navigate, getDashboardTargetUrl]
  );

  // Background auto-polling every 3 seconds
  useEffect(() => {
    const pollTimer = setInterval(async () => {
      try {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            clearInterval(pollTimer);
            handleCheckEmailVerification(false);
          }
        }
      } catch (_) {
        // Ignore background polling errors
      }
    }, 3000);

    return () => clearInterval(pollTimer);
  }, [handleCheckEmailVerification]);

  const handleResendVerificationEmail = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError(null);
    try {
      if (auth.currentUser) {
        const actionSettings = {
          url: `${window.location.origin}/dashboard`,
          handleCodeInApp: true,
        };
        await sendEmailVerification(auth.currentUser, actionSettings);
        setResendCooldown(60);
        toast.success("A new verification link has been sent to your email.");
      } else {
        toast.error("User session expired. Please try signing up again.");
      }
    } catch (err) {
      console.error("Resend verification error:", err);
      toast.error(err.message || "Failed to resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col justify-start">
      {/* Back Button */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-800 hover:text-black mb-2 transition-colors w-fit focus:outline-none cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          <span>Back</span>
        </button>
      )}

      {/* Heading */}
      <h3 className="text-xl sm:text-2xl font-extrabold text-[#111827] tracking-tight leading-tight mb-1">
        Verify Your Email
      </h3>

      {/* Verification Visual */}
      <div className="flex justify-center my-3">
        <div className="w-14 h-14 rounded-full bg-[#E0F2FE] flex items-center justify-center text-[#009DFF] shadow-sm">
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
            />
          </svg>
        </div>
      </div>

      {/* Subheading */}
      <p className="text-xs text-gray-500 font-medium mb-4 text-center leading-relaxed">
        We've sent a verification link to <span className="font-bold text-gray-800">{email}</span>. Please check your
        inbox and click the link to activate your account.
      </p>

      {error && (
        <div className="text-[#E5383B] text-xs font-semibold mb-2.5 text-center">{error}</div>
      )}

      {/* Action Button */}
      <div className="w-full mt-1">
        <Button
          version="secondary"
          text="I've Verified My Email"
          type="button"
          onClick={() => handleCheckEmailVerification(true)}
          loading={checkingVerification}
          width="100%"
          className="w-full justify-center font-extrabold"
        />
      </div>

      {/* Resend Section */}
      <div className="flex flex-col items-center justify-center mt-5 text-center w-full select-none">
        <p className="text-xs text-[#64748B] font-medium">
          Didn't receive the email?{" "}
          {resendCooldown === 0 ? (
            <button
              type="button"
              onClick={handleResendVerificationEmail}
              disabled={resendLoading}
              className="text-[#009DFF] hover:underline font-bold focus:outline-none cursor-pointer"
            >
              Resend
            </button>
          ) : (
            <span className="text-[#94A3B8] font-bold">Resend</span>
          )}
        </p>

        {resendCooldown > 0 ? (
          <div className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-[#F1F5F9] text-[#475569] text-xs font-bold shadow-sm">
            Resend in 00:{String(resendCooldown).padStart(2, "0")}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleResendVerificationEmail}
            disabled={resendLoading}
            className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-[#009DFF]/10 hover:bg-[#009DFF]/20 text-[#009DFF] text-xs font-bold transition-colors cursor-pointer"
          >
            {resendLoading ? "Sending..." : "Resend Link"}
          </button>
        )}
      </div>

      {/* Change Email Link */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-4 text-xs font-bold text-gray-400 hover:text-gray-700 hover:underline block mx-auto transition duration-200 cursor-pointer"
        >
          ← Change email address
        </button>
      )}
    </div>
  );
};

export default EmailVerificationSentView;
